import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { DictionaryService } from '../../../../services/dictionary.service';
import { MAX_TICKET_FILE_SIZE } from '../../domain/ticket.models';
import { ButtonDirective } from '../../../../components/button/button.directive';
import { ensureTicketAudioDuration } from '../../domain/ticket-audio-duration';

@Component({
  selector: 'app-ticket-audio-recorder',
  standalone: true,
  imports: [CommonModule, ButtonDirective, NgIcon],
  template: `
    <div class="recorder">
      @if (!recording() && !file()) {
        <button buttonTheme="light" buttonType="regular" size="sm" type="button" (click)="start()">
          <ng-icon name="microphone" size="18"></ng-icon>
          <span>{{ dictionary.get('RecordVoice') }}</span>
        </button>
      }
      @if (recording()) {
        <span class="recording-dot"></span><span>{{ elapsedLabel() }}</span>
        <button buttonTheme="danger" buttonType="regular" size="sm" type="button" (click)="stop()">{{ dictionary.get('StopRecording') }}</button>
      }
      @if (file()) {
        <div class="recording-preview">
          <audio
            controls
            preload="metadata"
            [src]="previewUrl()"
            (loadedmetadata)="ensurePreviewDuration($event)"
          ></audio>
          <div class="preview-actions">
            <button buttonTheme="light" buttonType="regular" size="sm" type="button" (click)="recordAgain()">
              {{ dictionary.get('Rerecord') }}
            </button>
            <button buttonTheme="light" buttonType="regular" size="sm" type="button" (click)="discard()">
              {{ dictionary.get('Remove') }}
            </button>
          </div>
        </div>
      }
      @if (error()) { <small class="error">{{ error() }}</small> }
    </div>
  `,
  styles: [`
    .recorder{display:flex;align-items:center;gap:var(--spacing-sm);flex-wrap:wrap}
    .recording-preview{display:flex;align-items:center;gap:var(--spacing-sm);flex-wrap:wrap}
    .preview-actions{display:flex;align-items:center;gap:var(--spacing-xs)}
    .recorder button{box-shadow:none}
    .recorder>button:first-child{direction:ltr;color:var(--jellyfish-700);background:var(--jellyfish-50);border:1px solid var(--jellyfish-200);border-radius:var(--spacing-sm)}
    .recorder>button:first-child:hover{background:var(--jellyfish-100);border-color:var(--jellyfish-400)}
    .recording-dot{width:10px;height:10px;border-radius:50%;background:var(--maroon-500);animation:pulse 1s infinite}
    audio{height:36px;max-width:min(100%,280px)}.error{color:var(--maroon-700);width:100%}
    @keyframes pulse{50%{opacity:.35}}
  `],
})
export class AudioRecorderComponent implements OnChanges, OnDestroy {
  readonly dictionary = inject(DictionaryService);
  readonly selectedFile = input<File | null>(null);
  readonly fileChange = output<File | null>();
  readonly recording = signal(false);
  readonly file = signal<File | null>(null);
  readonly previewUrl = signal('');
  readonly error = signal('');
  readonly elapsedSeconds = signal(0);
  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private timer?: ReturnType<typeof setInterval>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedFile'] && this.selectedFile() !== this.file()) {
      this.setFile(this.selectedFile());
    }
  }

  elapsedLabel(): string {
    const value = this.elapsedSeconds();
    return `${Math.floor(value / 60).toString().padStart(2, '0')}:${(value % 60).toString().padStart(2, '0')}`;
  }

  ensurePreviewDuration(event: Event): void {
    ensureTicketAudioDuration(event.currentTarget as HTMLAudioElement);
  }

  async start(): Promise<void> {
    this.error.set('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.error.set(this.dictionary.get('RecordingUnsupported'));
      return;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
        .find((type) => MediaRecorder.isTypeSupported(type));
      this.chunks = [];
      this.recorder = mimeType ? new MediaRecorder(this.stream, { mimeType }) : new MediaRecorder(this.stream);
      this.recorder.ondataavailable = ({ data }) => { if (data.size) this.chunks.push(data); };
      this.recorder.onerror = () => this.fail(this.dictionary.get('RecordingInterrupted'));
      this.recorder.onstop = () => this.finish();
      this.elapsedSeconds.set(0);
      this.recording.set(true);
      this.recorder.start(1000);
      this.timer = setInterval(() => {
        const next = this.elapsedSeconds() + 1;
        this.elapsedSeconds.set(next);
        if (next >= 300) this.stop();
      }, 1000);
    } catch {
      this.error.set(this.dictionary.get('MicrophoneUnavailable'));
      this.release();
    }
  }

  stop(): void { if (this.recorder?.state === 'recording') this.recorder.stop(); }
  async recordAgain(): Promise<void> {
    this.discard();
    await this.start();
  }
  discard(): void {
    this.setFile(null); this.fileChange.emit(null); this.error.set('');
  }
  ngOnDestroy(): void {
    if (this.recorder?.state === 'recording') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.clearTimer(); this.release(); this.clearPreview();
  }

  private finish(): void {
    this.recording.set(false); this.clearTimer(); this.release();
    const type = (this.recorder?.mimeType || this.chunks[0]?.type || 'audio/webm').split(';')[0];
    const blob = new Blob(this.chunks, { type });
    if (!blob.size) return this.fail(this.dictionary.get('EmptyRecording'));
    if (blob.size > MAX_TICKET_FILE_SIZE) return this.fail(this.dictionary.get('FileTooLarge'));
    const extension = type.includes('ogg') ? 'ogg' : type.includes('mp4') ? 'm4a' : 'webm';
    const file = new File([blob], `ticket-recording-${Date.now()}.${extension}`, { type });
    this.setFile(file); this.fileChange.emit(file);
  }
  private fail(message: string): void {
    this.recording.set(false); this.clearTimer(); this.release(); this.error.set(message);
  }
  private clearTimer(): void { if (this.timer) clearInterval(this.timer); this.timer = undefined; }
  private release(): void { this.stream?.getTracks().forEach((track) => track.stop()); this.stream = undefined; }
  private setFile(file: File | null): void {
    this.clearPreview();
    this.file.set(file);
    if (file) this.previewUrl.set(URL.createObjectURL(file));
  }
  private clearPreview(): void { if (this.previewUrl()) URL.revokeObjectURL(this.previewUrl()); this.previewUrl.set(''); }
}
